import { useEffect, useRef, useState } from "react";
import { gateway } from "../../gateway";
import { Backdrop, Box, Button, CircularProgress, Modal, TextField, Typography } from "@mui/material";
import { Email, SecurityOutlined, Sms } from "@mui/icons-material";
import { colors } from "../ui/colors";

export const TwoFaModal = ({ open, onClose, email, userPhone, onVerify, isVerifying, isDisabled, password, user }) => {
  const [step, setStep] = useState("method");
  const [method, setMethod] = useState(null);
  const [destination, setDestination] = useState("");
  const [sending, setSending] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      setShowContent(false);
      setTimeout(() => setShowContent(true), 200);
      setStep("method");
      setMethod(null);
      setDestination("");
      setCode(["", "", "", "", ""]);
      setIsCodeComplete(false);
    } else {
      setShowContent(false);
    }
  }, [open]);

  const handleSend = async (selected) => {
    setMethod(selected);
    setDestination(selected === "email" ? email : user?.phone || "");
    setStep("code");
    setSending(true);
    try {
      await gateway.post("/users/signin-send-twofa", { email, password, twoFaMethod: selected }, { withCredentials: true });
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[index] = value;
    setCode(next);
    setIsCodeComplete(next.every((d) => d.length === 1));
    if (value && index < code.length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) inputsRef.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, code.length);
    if (pasted) {
      const arr = pasted.split("");
      while (arr.length < code.length) arr.push("");
      setCode(arr);
      setIsCodeComplete(arr.every((d) => d.length === 1));
      inputsRef.current[Math.min(pasted.length, code.length - 1)]?.focus();
    }
  };

  const submitCode = () => {
    if (isCodeComplete && !isVerifying) onVerify(email, code.join(""));
  };

  return (
    <Modal
      open={open}
      onClose={() => { }}
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.45)" } } }}
      disableAutoFocus
      closeAfterTransition
      disableEnforceFocus
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            borderRadius: "16px",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            p: { xs: 4, md: 6 },
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            border: "1px solid rgba(251, 142, 0, 0.12)",
            transform: open && showContent ? "translateY(0)" : "translateY(20px)",
            opacity: open && showContent ? 1 : 0,
            transition: "all 360ms ease-out",
          }}
        >
          <Box sx={{ display: "inline-flex", mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`,
                boxShadow: "0 8px 24px rgba(251,142,0,0.25)",
              }}
            >
              <SecurityOutlined sx={{ fontSize: 40, color: "white" }} />
            </Box>
          </Box>

          {step === "method" && (
            <>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "28px", md: "32px" },
                  mb: 2,
                  color: colors.gray[800],
                }}
              >
                New Login Detected
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    color: colors.gray[700],
                    fontSize: { xs: "18px", md: "20px" },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Verify Your Identity
                </Typography>

                <Typography
                  sx={{
                    color: colors.gray[600],
                    fontSize: { xs: "15px", md: "16px" },
                    lineHeight: "normal",
                  }}
                >
                  For your security, we've sent a verification code to your registered
                  email or phone. Please enter the code below to continue.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
                <Button
                  onClick={() => handleSend("email")}
                  disabled={sending}
                  sx={{
                    borderRadius: "12px",
                    height: "64px",
                    px: 4,
                    background: "transparent",
                    border: `2px solid ${colors.orange[100]}`,
                    color: colors.gray[700],
                    fontWeight: 600,
                    fontSize: "16px",
                    textTransform: "none",
                    transition: "all 200ms ease-out",
                    "&:hover": {
                      background: "rgba(251,142,0,0.04)",
                      border: `2px solid ${colors.orange.base}`,
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 16px rgba(251,142,0,0.15)",
                    },
                  }}
                  variant="outlined"
                  disableElevation
                  fullWidth
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "flex-start" }}>
                    <Box
                      sx={{
                        width: 45,
                        height: 45,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(251,142,0,0.1)",
                      }}
                    >
                      <Email sx={{ fontSize: 20, color: colors.orange.base }} />
                    </Box>
                    <Box sx={{ textAlign: "left"}}>
                      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.gray[800] }}>EMAIL</Typography>
                      <Typography sx={{ lineHeight: "normal", fontSize: "14px", color: colors.gray[500] }}>{email ? `Send to ${email}` : "Verify via email address"}</Typography>
                    </Box>
                  </Box>
                </Button>

                <Button
                  onClick={() => handleSend("phone")}
                  disabled={sending}
                  sx={{
                    borderRadius: "12px",
                    height: "64px",
                    px: 4,
                    background: "transparent",
                    border: `2px solid ${colors.orange[100]}`,
                    color: colors.gray[700],
                    fontWeight: 600,
                    fontSize: "16px",
                    textTransform: "none",
                    transition: "all 200ms ease-out",
                    "&:hover": {
                      background: "rgba(251,142,0,0.04)",
                      border: `2px solid ${colors.orange.base}`,
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 16px rgba(251,142,0,0.15)",
                    },
                  }}
                  variant="outlined"
                  disableElevation
                  fullWidth
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "flex-start" }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(251,142,0,0.1)",
                      }}
                    >
                      <Sms sx={{ fontSize: 20, color: colors.orange.base }} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.gray[800] }}>SMS</Typography>
                      <Typography sx={{ fontSize: "14px", color: colors.gray[500] }}>
                        {user?.phone ? `Send to ${user.phone}` : "Verify via text message"}
                      </Typography>

                    </Box>
                  </Box>
                </Button>
              </Box>

              <Button
                fullWidth
                variant="text"
                onClick={onClose}
                sx={{
                  py: 1,
                  color: colors.orange.base,
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: colors.orange[50] },
                }}
              >
                Cancel
              </Button>
            </>
          )}

          {step === "code" && (
            <>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", md: "30px" }, mb: 2, color: colors.gray[800] }}>
                Enter Verification Code
              </Typography>

              <Typography
                sx={{
                  color: colors.gray[600],
                  fontSize: { xs: "15px", md: "16px" },
                  fontWeight: 500,
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                We sent a code to your {method === "email" ? "email" : "phone"}: <br />
                <strong>{destination}</strong>
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4 }}>
                {code.map((digit, index) => (
                  <TextField
                    key={index}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    inputRef={(ref) => (inputsRef.current[index] = ref)}
                    inputProps={{ maxLength: 1, inputMode: "numeric", style: { textAlign: "center", fontSize: "20px", fontWeight: 600 } }}
                    disabled={isDisabled}
                    sx={{
                      width: 56,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: colors.warmWhite.base,
                        "& fieldset": { borderColor: colors.orange[200], borderWidth: "2px" },
                        "&:hover fieldset": { borderColor: colors.orange[400] },
                        "&.Mui-focused fieldset": { borderColor: colors.orange.base },
                      },
                    }}
                  />
                ))}
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={isVerifying || isDisabled || !isCodeComplete}
                onClick={submitCode}
                sx={{
                  py: 1.5,
                  mb: 3,
                  borderRadius: "12px",
                  backgroundColor: colors.inverse.base,
                  color: colors.inverse.text,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
                  "&:disabled": { backgroundColor: colors.gray[200], color: colors.gray[400] },
                }}
              >
                {isVerifying ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setCode(["", "", "", "", ""]);
                  setIsCodeComplete(false);
                  setStep("method");
                }}
                sx={{
                  py: 1,
                  color: colors.orange.base,
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: colors.orange[50] },
                }}
              >
                Choose a different method
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
